import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Platform,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { Card, Avatar } from '@cubemeet/ui';
import { MODULE_COLORS } from '@cubemeet/core/theme';
import { supabase } from '@cubemeet/core/services/supabase';
import { useTravelI18n } from './useTravelI18n';

type ExpenseMode = 'prepaid' | 'actual_expense';

type TravelMember = {
  user_id: string;
  share_ratio?: number | null;
  profile?: {
    display_name?: string | null;
    avatar_url?: string | null;
  } | null;
};

type TravelDetailShape = {
  id: string;
  name?: string | null;
  creator_id?: string | null;
  payment_mode?: string | null;
  currency?: string | null;
  members?: TravelMember[] | null;
};

interface ExpenseSettingsProps {
  groupId: string;
  travel: TravelDetailShape | null;
  userId: string;
  onBack: () => void;
  onSaved: () => Promise<void> | void;
}

const ExpenseSettings: React.FC<ExpenseSettingsProps> = ({ groupId, travel, userId, onBack, onSaved }) => {
  const { t } = useTravelI18n();
  const [mode, setMode] = useState<ExpenseMode>('actual_expense');
  const [useShares, setUseShares] = useState(false);
  const [memberShares, setMemberShares] = useState<Record<string, number>>({});
  const [memberShareInputs, setMemberShareInputs] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const isCreator = Boolean(travel?.creator_id && travel.creator_id === userId);
  const members = travel?.members ?? [];

  const showAlert = useCallback((title: string, message?: string) => {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') {
        const text = message ? `${title}\n${message}` : title;
        window.alert(text);
      }
      return;
    }
    // eslint-disable-next-line no-alert
    alert(message ? `${title}\n${message}` : title);
  }, []);

  useEffect(() => {
    if (!travel) return;

    const paymentMode = travel.payment_mode || '';
    const nextMode: ExpenseMode = paymentMode.includes('prepay') ? 'prepaid' : 'actual_expense';
    const nextUseShares = paymentMode.includes('share');
    setMode(nextMode);
    setUseShares(nextUseShares);

    const shares: Record<string, number> = {};
    const inputs: Record<string, string> = {};
    (travel.members || []).forEach((member) => {
      if (!member?.user_id) return;
      const ratio = typeof member.share_ratio === 'number' ? member.share_ratio : 1;
      shares[member.user_id] = ratio;
      inputs[member.user_id] = ratio.toFixed(1);
    });

    setMemberShares(shares);
    setMemberShareInputs(inputs);
  }, [travel]);

  const totalShares = useMemo(() => {
    return Object.values(memberShares).reduce((sum, value) => sum + value, 0);
  }, [memberShares]);

  const shareDescriptions = useMemo(() => {
    const messages: Record<string, string> = {
      prepaidWithShares: t('expenseSettingsModal.shareSection.descriptions.prepaidWithShares') || '根据份额结算预付金额',
      prepaidWithoutShares: t('expenseSettingsModal.shareSection.descriptions.prepaidWithoutShares') || '所有人均分预付金额',
      actualExpenseWithShares: t('expenseSettingsModal.shareSection.descriptions.actualExpenseWithShares') || '按份额分摊实际费用',
      actualExpenseWithoutShares: t('expenseSettingsModal.shareSection.descriptions.actualExpenseWithoutShares') || '所有实际费用均分',
    };

    if (mode === 'prepaid' && useShares) return messages.prepaidWithShares;
    if (mode === 'prepaid') return messages.prepaidWithoutShares;
    if (useShares) return messages.actualExpenseWithShares;
    return messages.actualExpenseWithoutShares;
  }, [mode, useShares, t]);

  const handleSelectShareMode = useCallback(
    (nextUseShares: boolean) => {
      if (!isCreator) return;
      setUseShares(nextUseShares);
    },
    [isCreator],
  );

  const adjustShare = useCallback(
    (userId: string, delta: number) => {
      if (!isCreator) return;
      setMemberShares((prev) => {
        const current = prev[userId] ?? 0;
        const next = Math.max(0, parseFloat((current + delta).toFixed(1)));
        return { ...prev, [userId]: next };
      });
      setMemberShareInputs((prev) => {
        const current = prev[userId] ?? '0.0';
        const parsed = parseFloat(current) || 0;
        const next = Math.max(0, parseFloat((parsed + delta).toFixed(1)));
        return { ...prev, [userId]: next.toFixed(1) };
      });
    },
    [isCreator],
  );

  const handleShareInputChange = useCallback((userId: string, value: string) => {
    if (!isCreator) return;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setMemberShareInputs((prev) => ({ ...prev, [userId]: value }));
    }
  }, [isCreator]);

  const handleShareInputBlur = useCallback(
    (userId: string) => {
      if (!isCreator) return;
      setMemberShareInputs((prev) => {
        const stored = prev[userId] ?? '0';
        const numeric = stored === '' || stored === '.' ? 0 : parseFloat(stored) || 0;
        const clamped = Math.max(0, parseFloat(numeric.toFixed(1)));
        setMemberShares((shares) => ({ ...shares, [userId]: clamped }));
        return { ...prev, [userId]: clamped.toFixed(1) };
      });
    },
    [isCreator],
  );

  const handleSave = useCallback(async () => {
    if (!isCreator) {
      showAlert(t('travelDetail.messages.error') || '错误', t('travelDetail.messages.insufficientPermission') || '仅创建者可修改设置');
      return;
    }

    setSaving(true);
    try {
      const paymentMode = mode === 'prepaid'
        ? (useShares ? 'prepaid_shares' : 'prepaid_equal')
        : (useShares ? 'actual_shares' : 'actual_equal');

      const { error: updateError } = await supabase
        .from('groups')
        .update({
          payment_mode: paymentMode,
          updated_at: new Date().toISOString(),
        })
        .eq('id', groupId);

      if (updateError) {
        throw updateError;
      }

      if (useShares && Object.keys(memberShares).length > 0) {
        const updates = Object.entries(memberShares).map(([memberId, ratio]) =>
          supabase
            .from('group_members')
            .update({ share_ratio: ratio })
            .eq('group_id', groupId)
            .eq('user_id', memberId),
        );
        const results = await Promise.all(updates);
        const updateError = results.find((result) => result.error);
        if (updateError?.error) {
          throw updateError.error;
        }
      }

      showAlert(t('travelDetail.messages.success') || '成功', t('travelDetail.messages.paymentSettingsSaved') || '费用设置已保存');
      await onSaved();
    } catch (error: any) {
      console.error('Failed to save expense settings', error);
      const message = error?.message || t('travelDetail.messages.saveShareSettingsFailed') || '保存失败，请稍后再试';
      showAlert(t('travelDetail.messages.error') || '错误', message);
    } finally {
      setSaving(false);
    }
  }, [groupId, isCreator, memberShares, mode, onSaved, showAlert, t, useShares]);

  const disableSave = !isCreator || saving;

  if (!travel) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={MODULE_COLORS.travel} />
        <Text style={styles.loadingText}>{t('travelDetail.loading.title') || '正在加载旅行详情…'}</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{t('travelDetail.modal.paymentSettings') || '支付设置'}</Text>
        <View style={styles.headerActions}>
          <TouchableOpacity
            style={[styles.iconButton, styles.checkButton, disableSave && styles.iconButtonDisabled]}
            onPress={handleSave}
            disabled={disableSave}
          >
            <Text style={styles.checkLabel}>{saving ? '...' : '\u2713'}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.iconButton} onPress={onBack}>
            <Icon name="close" size={18} color="#6B7280" />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
        {!isCreator && (
          <Card style={styles.noticeCard}>
            <View style={styles.noticeRow}>
              <Icon name="info" size={20} color="#F59E0B" />
              <Text style={styles.noticeText}>
                {t('expenseSettingsModal.permissionNotice') || '仅旅行创建者可以修改费用设置'}
              </Text>
            </View>
          </Card>
        )}

        <Card style={styles.modalSection}>
          <Text style={styles.modalSectionTitle}>{t('travelDetail.modal.paymentMode') || '支付模式'}</Text>
          <Text style={styles.modalSectionDesc}>
            {t('travelDetail.modal.currentProjectUses') || '当前项目使用'}{' '}
            {mode === 'prepaid'
              ? t('travelDetail.modal.prepaidMode') || '预付模式 (旅行社模式)' 
              : t('travelDetail.modal.actualExpenseMode') || '实际费用模式 (按实际费用分配)'}
          </Text>

          <View style={styles.shareSelector}>
            <TouchableOpacity
              style={[styles.shareOption, !useShares && styles.shareOptionActive, !isCreator && styles.shareOptionDisabled]}
              onPress={() => handleSelectShareMode(false)}
              disabled={!isCreator}
            >
              <Text style={[styles.shareOptionText, !useShares && styles.shareOptionTextActive]}>
                {t('travelDetail.modal.averageMode') || '均分模式'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.shareOption, useShares && styles.shareOptionActive, !isCreator && styles.shareOptionDisabled]}
              onPress={() => handleSelectShareMode(true)}
              disabled={!isCreator}
            >
              <Text style={[styles.shareOptionText, useShares && styles.shareOptionTextActive]}>
                {t('travelDetail.modal.shareMode') || '按份模式'}
              </Text>
            </TouchableOpacity>
          </View>

          <View style={styles.shareDescriptionBlock}>
            <Text style={styles.shareDescriptionTitle}>
              {t('expenseSettingsModal.shareSection.title') || '份额设置'}
            </Text>
            <Text style={styles.shareDescriptionText}>{shareDescriptions}</Text>
          </View>
        </Card>

        {useShares && members.length > 0 ? (
          <Card style={styles.modalSection}>
            <Text style={styles.modalSectionTitle}>
              {t('travelDetail.modal.memberShareSettings') || '成员份额设置'}
            </Text>
            <Text style={styles.modalSectionDesc}>
              {t('travelDetail.modal.memberShareDescription') || '为每位成员设置份额比例。'}
            </Text>

            {members.map((member) => {
              const userIdKey = member.user_id;
              const inputValue = memberShareInputs[userIdKey] ?? '1.0';
              const disabled = !isCreator;
              return (
                <View key={userIdKey} style={styles.memberRow}>
                  <View style={styles.memberInfo}>
                    <Avatar
                      size="small"
                      avatarUrl={member.profile?.avatar_url || undefined}
                      displayName={member.profile?.display_name || undefined}
                      style={styles.memberAvatar}
                    />
                    <Text style={styles.memberName}>
                      {member.profile?.display_name || t('travelDetail.content.unknownUser') || '成员'}
                    </Text>
                  </View>
                  <View style={styles.shareControls}>
                    <TouchableOpacity
                      style={[styles.shareButton, disabled && styles.shareButtonDisabled]}
                      onPress={() => adjustShare(userIdKey, -0.1)}
                      disabled={disabled}
                    >
                      <Text style={[styles.shareSymbol, disabled && styles.shareSymbolDisabled]}>-</Text>
                    </TouchableOpacity>
                    <TextInput
                      style={[styles.shareInput, disabled && styles.shareInputDisabled]}
                      value={inputValue}
                      onChangeText={(text) => handleShareInputChange(userIdKey, text)}
                      onBlur={() => handleShareInputBlur(userIdKey)}
                      keyboardType="numeric"
                      editable={!disabled}
                    />
                    <TouchableOpacity
                      style={[styles.shareButton, disabled && styles.shareButtonDisabled]}
                      onPress={() => adjustShare(userIdKey, 0.1)}
                      disabled={disabled}
                    >
                      <Text style={[styles.shareSymbol, disabled && styles.shareSymbolDisabled]}>+</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })}

            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>
                {t('travelDetail.modal.totalShares') || '总份额'}: {totalShares.toFixed(1)}
              </Text>
            </View>
          </Card>
        ) : null}

        <View style={styles.bottomSpacer} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  scroll: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 20,
    paddingVertical: 24,
    gap: 16,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#4B5563',
  },
  noticeCard: {
    padding: 16,
    borderRadius: 16,
  },
  noticeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  noticeText: {
    flex: 1,
    fontSize: 14,
    color: '#9A6700',
  },
  modalSection: {
    padding: 20,
    borderRadius: 16,
    gap: 16,
  },
  modalSectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
  },
  modalSectionDesc: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  shareSelector: {
    flexDirection: 'row',
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    padding: 4,
    gap: 8,
  },
  shareOption: {
    flex: 1,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
  },
  shareOptionActive: {
    backgroundColor: '#FFFFFF',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 6,
        elevation: 3,
      }
    }),
  },
  shareOptionDisabled: {
    opacity: 0.6,
  },
  shareOptionText: {
    fontSize: 15,
    color: '#6B7280',
    fontWeight: '600',
  },
  shareOptionTextActive: {
    color: MODULE_COLORS.travel,
  },
  shareDescriptionBlock: {
    backgroundColor: '#FFF6F0',
    borderRadius: 12,
    padding: 16,
    gap: 4,
  },
  shareDescriptionTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  shareDescriptionText: {
    fontSize: 13,
    color: '#6B7280',
    lineHeight: 19,
  },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#E5E7EB',
  },
  memberInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  memberAvatar: {
    marginRight: 0,
  },
  memberName: {
    fontSize: 15,
    color: '#111827',
    fontWeight: '500',
  },
  shareControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  shareButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: MODULE_COLORS.travel,
    alignItems: 'center',
    justifyContent: 'center',
  },
  shareButtonDisabled: {
    borderColor: '#D1D5DB',
  },
  shareInput: {
    width: 64,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    textAlign: 'center',
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#FFFFFF',
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  shareInputDisabled: {
    backgroundColor: '#F3F4F6',
    color: '#9CA3AF',
  },
  totalRow: {
    marginTop: 16,
    alignItems: 'flex-end',
  },
  totalLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: MODULE_COLORS.travel,
  },
  bottomSpacer: {
    height: 32,
  },
  header: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  iconButton: {
    width: 36,
    height: 36,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  checkButton: {
    backgroundColor: MODULE_COLORS.travel,
    borderColor: MODULE_COLORS.travel,
  },
  checkLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: '#FFFFFF',
    transform: [{ translateY: Platform.select({ web: -1, default: -0.5 }) }],
  },
  iconButtonDisabled: {
    opacity: 0.5,
  },
  shareSymbol: {
    fontSize: 16,
    fontWeight: '700',
    color: MODULE_COLORS.travel,
    textAlign: 'center',
  },
  shareSymbolDisabled: {
    color: '#9CA3AF',
  },
});

export default ExpenseSettings;
